import type { TFunction } from 'i18next';

import { RoleApiError } from '../types/role.types';

// Operation-specific fallback message keys (PRD 16 §8.2: create/save/delete
// failures surface a dedicated message instead of the generic "unknown").
type FallbackKey =
  | 'errors.unknown'
  | 'errors.createFailed'
  | 'errors.saveFailed'
  | 'errors.deleteFailed'
  | 'errors.loadInfo';

// System-error codes carry a GENERIC server message ("알 수 없는 오류가 발생했습니다." — the
// admin-api INTERNAL_ERROR catalogue entry), which says nothing about which operation failed.
// PRD §8.2 wants the operation-specific copy there, so these never fall through to `e.message`.
const SYSTEM_ERROR_CODES = new Set(['INTERNAL_ERROR', 'NETWORK', 'UNKNOWN']);

// Maps a RoleApiError to a localized message via the `roles` namespace.
// Recognised business/field codes map to their specific message; unrecognised
// failures fall back to `fallbackKey` (operation-specific where provided).
export function getRoleErrorMessage(
  e: unknown,
  t: TFunction<'roles'>,
  fallbackKey: FallbackKey = 'errors.unknown',
): string {
  if (e instanceof RoleApiError) {
    switch (e.code) {
      case 'ROLE_NAME_INVALID':
        return e.fields?.name ?? t('errors.nameInvalid');
      case 'ROLE_NAME_DUPLICATE':
        return e.fields?.name ?? t('errors.nameDuplicate');
      case 'RESERVED_ROLE':
        return t('errors.reservedRole');
      case 'SEED_ROLE_NOT_DELETABLE':
        return t('errors.seedNotDeletable');
      case 'ROLE_IN_USE_BY_SELF':
        return t('errors.inUseBySelf');
      case 'INSUFFICIENT_PERMISSION':
        return t('errors.insufficientPermission');
      case 'NOT_FOUND':
        return t('errors.loadInfo');
      default:
        // SHCT-631 (re-test) — the first fix only covered a body-less 5xx, where `message` is ''.
        // A 5xx WITH the standard envelope carries the generic INTERNAL_ERROR text, and preferring
        // it here shadowed the PRD §8.2 copy — exactly the "generic error" QC still saw on delete.
        return SYSTEM_ERROR_CODES.has(e.code) ? t(fallbackKey) : e.message || t(fallbackKey);
    }
  }
  return t(fallbackKey);
}

export function isFieldNameError(e: unknown): boolean {
  return (
    e instanceof RoleApiError &&
    (e.code === 'ROLE_NAME_INVALID' || e.code === 'ROLE_NAME_DUPLICATE')
  );
}
